/**
 * Landing — Ace brand page, library.orbitlens.io voice (Phase 2).
 *
 * Mirrors Login's design language across a scrolling narrative:
 *   - Fixed 9-dot starfield on deep dark (--bg-void).
 *   - Hero lives in a frosted glass card (same frame spec as Login).
 *   - Subsequent sections are naked typography on the starfield,
 *     separated by 1px --border dividers (library voice).
 *   - Type scale: Inter Tight hero, Inter sans body, JetBrains Mono
 *     for eyebrow and axis labels.
 *   - Color: ivory warm text + amber gold accent only. No gradients,
 *     no emoji, no colorful axis dots (L0 Quiet Rigor).
 */

import { useEffect } from "react";
import { track } from "../../lib/posthog";
import { startGitHubAuth } from "../../lib/githubAuth";
import { AceLogoMark } from "../../components/AceLogoMark";

export default function Landing() {
  useEffect(() => {
    track.landingViewed();
  }, []);

  // Plain sign-in entry — no return path, lands on the default post-login page.
  const handleGitHubLogin = () => startGitHubAuth();

  return (
    <>
      <div className="starfield" aria-hidden="true" />

      <main
        className="relative z-10"
        style={{ fontFamily: "var(--sans)", color: "var(--text)" }}
      >
        <HeroSection onGitHubLogin={handleGitHubLogin} />
        <PhilosophySection />
        <AxesSection />
        <TopologySection />
        <TimeTravelSection />
        <FortyPointSection />
        <NotSection />
        <CtaSection onGitHubLogin={handleGitHubLogin} />
        <FooterMotto />
      </main>
    </>
  );
}

/* =========================================================================
 * Shared style fragments
 * ========================================================================= */

const eyebrowStyle = {
  fontFamily: "var(--mono)",
  fontSize: "11px",
  letterSpacing: "3.5px",
  color: "var(--text-muted)",
  textTransform: "uppercase" as const,
};

const sectionTitleStyle = {
  fontFamily: "var(--display)",
  fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
  fontWeight: 700,
  letterSpacing: "-0.03em",
  lineHeight: 1.15,
  color: "var(--text-strong)",
};

const ledeStyle = {
  fontSize: "15px",
  lineHeight: 1.75,
  fontWeight: 300,
  color: "var(--text-muted)",
};

const dividerStyle = {
  height: "1px",
  background: "var(--border)",
  border: "none",
  margin: "0 auto",
  maxWidth: "56rem",
};

/* =========================================================================
 * Hero — glass card, matches Login rhythm
 * ========================================================================= */

function HeroSection({ onGitHubLogin }: { onGitHubLogin: () => void }) {
  return (
    <section className="flex min-h-screen items-center justify-center px-2xl py-6xl">
      <div
        className="flex w-full max-w-[42rem] flex-col items-center"
        style={{
          background: "rgba(10, 14, 23, 0.72)",
          border: "1px solid var(--border)",
          borderRadius: "20px",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: "20px 32px 36px",
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
        }}
      >
        <div
          className="flex items-center gap-md"
          style={{ ...eyebrowStyle, marginBottom: "40px" }}
        >
          <span>orbitlens</span>
          <span style={{ color: "var(--text-dim)" }}>/</span>
          <span>ace</span>
        </div>

        <AceLogoMark glow="hero" className="mb-3xl h-40 w-40" />

        <h1
          style={{
            fontFamily: "var(--display)",
            fontSize: "clamp(2rem, 4vw, 2.75rem)",
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1.08,
            color: "var(--text-strong)",
            textAlign: "center",
            marginBottom: "16px",
          }}
        >
          OrbitLens{" "}
          <span style={{ fontWeight: 600, color: "var(--gold)" }}>Ace</span>
        </h1>

        <p
          className="text-center"
          style={{
            fontFamily: "var(--display)",
            fontSize: "clamp(0.9375rem, 1.6vw, 1.0625rem)",
            fontWeight: 400,
            letterSpacing: "-0.01em",
            color: "var(--text-muted)",
            marginBottom: "8px",
          }}
        >
          the Git Telescope
        </p>

        <p
          className="max-w-[28rem] text-center"
          style={{
            fontSize: "14px",
            lineHeight: 1.65,
            fontWeight: 300,
            color: "var(--text-muted)",
            marginBottom: "28px",
          }}
        >
          Git records commits.
          <br />
          We reveal the structure of the code universe.
          <br />
          <span style={{ fontStyle: "italic", color: "var(--text-dim)" }}>
            It doesn&apos;t measure activity. It measures gravity.
          </span>
        </p>

        <div className="flex flex-wrap items-center justify-center gap-2xl">
          <GoldButton onClick={onGitHubLogin}>Start observing</GoldButton>
          <a
            href="https://github.com/machuz/eis"
            className="transition-colors duration-150 ease-out"
            style={{
              fontFamily: "var(--mono)",
              fontSize: "0.8125rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "var(--text-muted)",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--text-strong)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            View on GitHub →
          </a>
        </div>
      </div>
    </section>
  );
}

/* =========================================================================
 * GoldButton — reusable, matches Login spec
 * ========================================================================= */

function GoldButton({
  onClick,
  children,
  withIcon = true,
}: {
  onClick: () => void;
  children: React.ReactNode;
  withIcon?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative inline-flex items-center gap-md border transition-colors duration-150 ease-out focus:outline-none focus-visible:outline-none"
      style={{
        fontFamily: "var(--mono)",
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "0.15em",
        textTransform: "uppercase",
        padding: "0.75rem 2rem",
        backgroundColor: "transparent",
        borderColor: "var(--gold)",
        color: "var(--gold)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "var(--gold-glow)";
        e.currentTarget.style.borderColor = "var(--gold-bright)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.borderColor = "var(--gold)";
      }}
    >
      {withIcon && (
        <svg
          className="h-5 w-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
          style={{ transform: "translateY(-1.5px)" }}
        >
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
        </svg>
      )}
      <span>{children}</span>
    </button>
  );
}

/* =========================================================================
 * Section wrapper — common heading + divider layout
 * ========================================================================= */

function SectionHeading({
  eyebrow,
  title,
  lede,
}: {
  eyebrow: string;
  title: string;
  lede?: string;
}) {
  return (
    <div className="mx-auto max-w-[42rem] text-center">
      <div
        style={{
          ...eyebrowStyle,
          marginBottom: "16px",
        }}
      >
        {eyebrow}
      </div>
      <h2 style={sectionTitleStyle}>{title}</h2>
      {lede && (
        <p style={{ ...ledeStyle, marginTop: "20px" }}>{lede}</p>
      )}
    </div>
  );
}

/* =========================================================================
 * Philosophy — 4 principles, no emoji
 * ========================================================================= */

const PRINCIPLES = [
  {
    eyebrow: "L0 · observation",
    title: "Observation, not generation",
    desc: "We don't predict. We don't infer signals from surveys. We read git log and git blame — the archaeological record of what actually survived. The telescope reveals what is already there.",
  },
  {
    eyebrow: "L0 · gravity",
    title: "Gravity, not activity",
    desc: "10,000 lines rewritten next month left no gravity. 500 lines that became the foundation shaped the universe. We measure what endures — exponentially time-decay weighted.",
  },
  {
    eyebrow: "L0 · durability",
    title: "Game-resistant by design",
    desc: "Commit splitting is measured by insertions, not commit count. Busy work doesn't help — only surviving code counts. Durability cannot be faked at the month scale.",
  },
  {
    eyebrow: "L0 · dark matter",
    title: "Dark matter acknowledged",
    desc: "Mentoring, culture, psychological safety — git cannot capture these. We know. This telescope observes gravity in code. The invisible forces matter too, but need a different instrument.",
  },
];

function PhilosophySection() {
  return (
    <>
      <hr style={dividerStyle} />
      <section className="mx-auto max-w-[64rem] px-2xl py-6xl">
        <SectionHeading
          eyebrow="software cosmology"
          title="Every codebase is a universe."
          lede="Engineers create gravity within it. Some write code that vanishes in weeks. Others write foundations that endure for years. The difference is invisible to commit counts — but not to a telescope."
        />
        <div className="mt-6xl grid gap-x-5xl gap-y-6xl md:grid-cols-2">
          {PRINCIPLES.map((p) => (
            <div key={p.title}>
              <div style={{ ...eyebrowStyle, marginBottom: "12px" }}>
                {p.eyebrow}
              </div>
              <h3
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.25,
                  color: "var(--text-strong)",
                  marginBottom: "12px",
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  lineHeight: 1.7,
                  fontWeight: 300,
                  color: "var(--text-muted)",
                }}
              >
                {p.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/* =========================================================================
 * 7 Axes of Impact
 * ========================================================================= */

const AXES = [
  {
    name: "Survival",
    weight: "25%",
    desc: "Does your code endure under change pressure? Time-decayed blame reveals what truly survives.",
  },
  {
    name: "Design",
    weight: "20%",
    desc: "Do you shape architectural boundaries? Commits to design-defining files.",
  },
  {
    name: "Catalysis",
    weight: "15%",
    desc: "Do others build on what you made? Surviving work later contributors layered on your still-living code.",
  },
  {
    name: "Debt cleanup",
    weight: "15%",
    desc: "Do you stabilize or destabilize? Fixing others' bugs vs generating new ones.",
  },
  {
    name: "Production",
    weight: "10%",
    desc: "Can you sustain high output? Measured by daily change velocity, not commit count.",
  },
  {
    name: "Breadth",
    weight: "10%",
    desc: "Can you operate across silos? Cross-repository structural presence.",
  },
  {
    name: "Indispensability",
    weight: "5%",
    desc: "Is the system fragile around you? Module ownership concentration — bus factor risk.",
  },
];

function AxesSection() {
  return (
    <>
      <hr style={dividerStyle} />
      <section className="mx-auto max-w-[56rem] px-2xl py-6xl">
        <SectionHeading
          eyebrow="seven orthogonal signals"
          title="Seven axes of impact."
          lede="Not one number. Seven directions that reveal different dimensions of engineering gravity."
        />
        <ul className="mt-6xl space-y-px">
          {AXES.map((axis) => (
            <li
              key={axis.name}
              className="grid grid-cols-[auto_1fr_auto] items-baseline gap-2xl border-t py-xl"
              style={{ borderColor: "var(--border)" }}
            >
              <span
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: "var(--text-strong)",
                  minWidth: "9rem",
                }}
              >
                {axis.name}
              </span>
              <span
                style={{
                  fontSize: "13.5px",
                  lineHeight: 1.6,
                  fontWeight: 300,
                  color: "var(--text-muted)",
                }}
              >
                {axis.desc}
              </span>
              <span
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  letterSpacing: "0.1em",
                  color: "var(--gold)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {axis.weight}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

/* =========================================================================
 * 3-Axis Engineer Topology
 * ========================================================================= */

const TOPOLOGY = [
  {
    eyebrow: "axis · role",
    title: "What you contribute",
    items: ["Architect", "Anchor", "Cleaner", "Producer", "Specialist"],
  },
  {
    eyebrow: "axis · style",
    title: "How you work",
    items: [
      "Builder",
      "Resilient",
      "Rescue",
      "Churn",
      "Mass",
      "Balanced",
      "Spread",
    ],
  },
  {
    eyebrow: "axis · state",
    title: "Your lifecycle phase",
    items: ["Active", "Growing", "Former", "Silent", "Fragile"],
  },
];

function TopologySection() {
  return (
    <>
      <hr style={dividerStyle} />
      <section className="mx-auto max-w-[64rem] px-2xl py-6xl">
        <SectionHeading
          eyebrow="engineer topology"
          title="Three independent dimensions."
          lede="Not a single label. Three orthogonal axes that capture what you contribute, how you work, and where you are in your lifecycle."
        />
        <div className="mt-6xl grid gap-5xl md:grid-cols-3">
          {TOPOLOGY.map((axis) => (
            <div key={axis.title}>
              <div style={{ ...eyebrowStyle, marginBottom: "12px" }}>
                {axis.eyebrow}
              </div>
              <h3
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.0625rem",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: "var(--text-strong)",
                  marginBottom: "20px",
                }}
              >
                {axis.title}
              </h3>
              <ul className="space-y-md">
                {axis.items.map((item) => (
                  <li
                    key={item}
                    style={{
                      fontFamily: "var(--mono)",
                      fontSize: "12px",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--text-muted)",
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p
          className="mx-auto mt-6xl max-w-[42rem] text-center"
          style={{
            fontSize: "14px",
            lineHeight: 1.75,
            fontWeight: 300,
            color: "var(--text-dim)",
            fontStyle: "italic",
          }}
        >
          Two engineers with identical total signals but different topology
          profiles are fundamentally different.
          <br />
          An Architect / Builder / Active cannot be replaced by a Producer /
          Mass / Silent.
        </p>
      </section>
    </>
  );
}

/* =========================================================================
 * Time Travel
 * ========================================================================= */

const TIME_SCALES = [
  {
    label: "Individual",
    desc: "Signal evolution, topology transitions, growth trajectory.",
  },
  {
    label: "Team",
    desc: "Role composition changes, generational leadership transfers.",
  },
  {
    label: "Organization",
    desc: "Health metrics, structural risk, cultural evolution.",
  },
];

function TimeTravelSection() {
  return (
    <>
      <hr style={dividerStyle} />
      <section className="mx-auto max-w-[48rem] px-2xl py-6xl">
        <SectionHeading
          eyebrow="gravity across time"
          title="The telescope sees through time."
          lede="Ace doesn't just show you where gravity is now. It shows you how gravity shifted across months and years — who built the foundation, who inherited it, who let it decay."
        />
        <ul className="mt-6xl space-y-px">
          {TIME_SCALES.map((scale) => (
            <li
              key={scale.label}
              className="grid grid-cols-[10rem_1fr] items-baseline gap-2xl border-t py-xl"
              style={{ borderColor: "var(--border)" }}
            >
              <span
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "var(--gold)",
                }}
              >
                {scale.label}
              </span>
              <span
                style={{
                  fontSize: "14px",
                  lineHeight: 1.65,
                  fontWeight: 300,
                  color: "var(--text-muted)",
                }}
              >
                {scale.desc}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

/* =========================================================================
 * 40-point bar
 * ========================================================================= */

function FortyPointSection() {
  return (
    <>
      <hr style={dividerStyle} />
      <section className="mx-auto max-w-[48rem] px-2xl py-6xl text-center">
        <div
          style={{
            ...eyebrowStyle,
            marginBottom: "24px",
          }}
        >
          the senior threshold
        </div>
        <p
          style={{
            fontFamily: "var(--display)",
            fontSize: "clamp(5rem, 12vw, 8rem)",
            fontWeight: 700,
            letterSpacing: "-0.05em",
            lineHeight: 1,
            color: "var(--gold)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          40
        </p>
        <p
          className="mt-2xl"
          style={{
            fontFamily: "var(--display)",
            fontSize: "clamp(1.125rem, 2vw, 1.375rem)",
            fontWeight: 500,
            letterSpacing: "-0.02em",
            color: "var(--text-strong)",
          }}
        >
          is genuinely senior.
        </p>
        <p
          className="mx-auto mt-2xl max-w-[36rem]"
          style={{
            fontSize: "14px",
            lineHeight: 1.75,
            fontWeight: 300,
            color: "var(--text-muted)",
          }}
        >
          Achieving 40+ across seven orthogonal axes simultaneously is
          intentionally hard. Easy metrics inflate everything. The multi-axis
          approach enforces rigor. The axes are normalized within a codebase, so
          40 is a genuinely high bar here — not a portable cross-company grade.
        </p>
      </section>
    </>
  );
}

/* =========================================================================
 * What this is NOT
 * ========================================================================= */

const NOT_STATEMENTS = [
  "Not a measure of human worth.",
  "Not a substitute for qualitative assessment.",
  "Not predictive of future performance.",
  "Not a sole hiring or firing criterion.",
];

function NotSection() {
  return (
    <>
      <hr style={dividerStyle} />
      <section className="mx-auto max-w-[42rem] px-2xl py-6xl">
        <SectionHeading
          eyebrow="boundary lines"
          title="What this is not."
        />
        <ul className="mt-5xl space-y-md">
          {NOT_STATEMENTS.map((line) => (
            <li
              key={line}
              style={{
                fontSize: "15px",
                lineHeight: 1.65,
                fontWeight: 300,
                color: "var(--text-muted)",
              }}
            >
              {line}
            </li>
          ))}
        </ul>
        <p
          className="mt-4xl"
          style={{
            fontSize: "14px",
            lineHeight: 1.75,
            fontWeight: 300,
            color: "var(--text-dim)",
            fontStyle: "italic",
          }}
        >
          It is a quantitative validation tool for intuition — revealing
          patterns human observation misses.
        </p>
      </section>
    </>
  );
}

/* =========================================================================
 * Final CTA
 * ========================================================================= */

function CtaSection({ onGitHubLogin }: { onGitHubLogin: () => void }) {
  return (
    <>
      <hr style={dividerStyle} />
      <section className="mx-auto max-w-[42rem] px-2xl py-6xl text-center">
        <h2 style={sectionTitleStyle}>See your engineering orbit.</h2>
        <p
          className="mt-2xl"
          style={{
            fontSize: "15px",
            lineHeight: 1.65,
            fontWeight: 300,
            color: "var(--text-muted)",
          }}
        >
          Every codebase is a universe. Ace is the observatory.
        </p>
        <div className="mt-5xl flex justify-center">
          <GoldButton onClick={onGitHubLogin}>Start observing</GoldButton>
        </div>
        <p
          className="mt-6xl"
          style={{
            fontFamily: "var(--mono)",
            fontSize: "11px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "var(--text-dim)",
          }}
        >
          powered by EIS — the open-source git telescope
        </p>
      </section>
    </>
  );
}

/* =========================================================================
 * Footer motto
 * ========================================================================= */

function FooterMotto() {
  return (
    <p
      className="py-4xl text-center uppercase"
      style={{
        fontFamily: "var(--mono)",
        fontSize: "10px",
        letterSpacing: "0.2em",
        color: "var(--text-dim)",
      }}
    >
      observe gravity · galaxy boundary · quiet rigor
    </p>
  );
}
